import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { neutral200, neutral300, neutral400 } from '../themes/colors';
import { useUniqueId } from '../utils/useUniqueId';

// The angled part of the Plecto logo, used as the repeating motif of every ring.
const MOTIF_PATH = 'M204.68 37.04L240.88 134.43L134.48 240.83L98.09 143.91L98.02 143.7L97.81 143.63'
	+ 'L0.89 107.24L107.55 0.57Z';
const MOTIF_VIEWBOX = 242;
const MOTIF_CENTER = MOTIF_VIEWBOX / 2;

const CANVAS = 100;
const RING_RADIUS = 100; // multiplied by the origin's reach to land just outside the frame
// ponytail: the brand videos are a ~10-ring halftone with motifs ~4x finer than this.
// Matched literally that is ~11k animated SVG paths, so the band is coarser here.
// Denser: raise the `rings` prop / lower MOTIF_SPACING, and watch the frame rate.
const MOTIF_SIZE = 0.5; // canvas units, as rendered, at the fattest point of the band
const MOTIF_SPACING = 1.8; // arc length between motifs, as rendered
// Measured off Patterns.png, the designer's pattern sheet: rings sit a constant ~30px apart
// and their ink coverage waxes and wanes between ~0.03 and ~0.36 over ~15-19 rings. So a band
// is a halftone — fat dots through the middle thinning to near-nothing at both edges, about a
// tenth of full size there.
// ponytail: the sheet also holds the dots a constant ~40px apart along each ring, so its dot
// count grows with radius. A ring here is one scaled copy of a fixed set of motifs, so ours
// thin out as the band travels instead. Fixing that means rebuilding the ring per frame.
const MOTIF_EDGE_SIZE = 0.05;
// The sheet's envelope spans ~15-19 rings. 10 is the compromise: enough to land the edge
// dots near the measured tenth of full size, without doubling the animated node count again.
// ponytail: raise toward 15 if the halftone still reads coarse and the frame rate holds.
export const DEFAULT_RINGS = 10; // concentric rings making up one band
// The designer builds each band from rings cycling through three greys.
const RING_COLORS = [neutral200, neutral300, neutral400];
const MIN_MOTIFS = 8;
const MAX_MOTIFS = 240; // ponytail: corner origins would otherwise double the node count
const FULL_TURN = 360;
const HALF_TURN = FULL_TURN / 2;
const DEG = Math.PI / HALF_TURN;
// Cadence measured off the brand animations. All three are the same travelling band; they
// differ only in how often one is fired and how many are in flight before it goes quiet.
// Pulse: 3 bands 0.8s apart in a 6s loop. Breathe (the "Breathing Idle" asset): 6 bands
// ~0.95s apart in a 10s loop, so the frame stays busy for half the loop instead of a fifth.
const PULSE_SECONDS = 6;
const BREATHE_SECONDS = 10;
const PULSE_INTERVAL_SECONDS = 0.8;
const BREATHE_INTERVAL_SECONDS = 0.95;
const PULSE_INTERVAL = PULSE_INTERVAL_SECONDS / PULSE_SECONDS;
const BREATHE_INTERVAL = BREATHE_INTERVAL_SECONDS / BREATHE_SECONDS;
// The centre asset fires 3 bands and then the frame clears; both corner assets fire 6 and
// stay populated for the whole loop. Their emissions actually alternate ~1.0s/1.5s apart —
// a uniform interval is close enough that the pairing does not read.
// The idle asset emits each band over a shorter window than the pulses, so its bands are
// visibly thinner even though it shares the corner geometry.
const BREATHE_BAND = 0.038;

export type PulseOrigin = 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type PulseAnimation = 'pulse' | 'breathe';

/**
 * Motif placements of one ring, centred on (0, 0). Sizes are divided by `reach` so the
 * rendered motifs come out the same size whatever distance the ring travels.
 * Exported for tests.
 */
export const ringMotifs = (reach: number): { x: number, y: number, angle: number }[] => {
	const spaced = Math.round((2 * Math.PI * RING_RADIUS * reach) / MOTIF_SPACING);
	const count = Math.min(MAX_MOTIFS, Math.max(MIN_MOTIFS, spaced));
	return Array.from({ length: count }, (_, i) => {
		const angle = (i / count) * FULL_TURN;
		return {
			x: Math.cos(angle * DEG) * RING_RADIUS,
			y: Math.sin(angle * DEG) * RING_RADIUS,
			angle,
		};
	});
};

/**
 * How fat this ring's motifs are, 0..1 across the band. Squared so the falloff is as steep
 * as the pattern sheets — a plain sine leaves the edge dots two thirds of full size.
 * Exported for tests.
 */
export const ringMotifSize = (ring: number, rings: number): number =>
	MOTIF_EDGE_SIZE + (1 - MOTIF_EDGE_SIZE) * Math.sin((Math.PI * (ring + 1)) / (rings + 1)) ** 2;

// [loop %, scale, opacity] read frame by frame off the brand videos. The steps carry the
// easing themselves so the animation can run linear — CSS would otherwise re-ease every
// keyframe interval. The centre burst accelerates hard and is gone by 32% of the loop; the
// corner burst runs nearly linear until 37.5%; the idle band is quickest off the mark and
// coasts to a stop by 35%.
/* eslint-disable no-magic-numbers -- sampled frame data */
const CENTRE_STEPS: [number, number, number][] = [
	[0, 0.02, 0],
	[2, 0.03, 1],
	[8, 0.07, 1],
	[16, 0.29, 1],
	[22, 0.62, 1],
	[26, 0.84, 0.55],
	[29, 0.93, 0.15],
	[32, 1, 0],
];
const CORNER_STEPS: [number, number, number][] = [
	[0, 0.02, 0],
	[1, 0.03, 1],
	[2.5, 0.05, 1],
	[7.5, 0.11, 1],
	[12.5, 0.21, 1],
	[17.5, 0.34, 1],
	[22.5, 0.48, 1],
	[26.5, 0.63, 1],
	[31, 0.79, 0.55],
	[34, 0.88, 0.15],
	[37.5, 1, 0],
];
const IDLE_STEPS: [number, number, number][] = [
	[0, 0.02, 0],
	[1, 0.03, 1],
	[5, 0.087, 1],
	[10, 0.223, 1],
	[15, 0.417, 1],
	[20, 0.65, 1],
	[25, 0.825, 1],
	[30, 0.922, 0.5],
	[33, 0.97, 0.15],
	[35, 1, 0],
];
/* eslint-enable no-magic-numbers */

const pulseFrames = (steps: [number, number, number][]) => keyframes`
	${steps.map(([pct, scale, opacity]) => `${pct}% { transform: scale(${scale}); opacity: ${opacity}; }`)
		.join('\n\t')}
	100% { transform: scale(1); opacity: 0; }
`;

const centrePulse = pulseFrames(CENTRE_STEPS);
const cornerPulse = pulseFrames(CORNER_STEPS);
const idleBand = pulseFrames(IDLE_STEPS);

// Aligning the viewBox with the origin keeps it anchored when the container is not square.
// Both corner videos fade their rings at ~208% of the frame HEIGHT — the 21:9 and the 16:9
// alike — and leave the far corner empty rather than covering it. The viewBox is drawn with
// `meet` so 100 units is the frame height on any landscape container, which is what keeps
// that distance the same across aspect ratios.
// ponytail: on a portrait container `meet` measures the width instead. The brand assets are
// all landscape; revisit if a tall one turns up.
// `band` is how long one pulse keeps emitting rows, as a fraction of the loop: the corner
// videos emit for ~0.5s of 10s, the centre one for ~0.11s of 6s.
// ponytail: per-origin because that is how the two brand assets differ. If an asset ever
// breaks the pattern, promote these to props rather than adding a third origin variant.
type Origin = {
	x: number, y: number, align: string, reach: number, band: number, bands: number,
	frames: ReturnType<typeof keyframes>,
};
const corner = { reach: 2.08, band: 0.06, bands: 6, frames: cornerPulse };
const origins: Record<PulseOrigin, Origin> = {
	center: {
		x: CANVAS / 2, y: CANVAS / 2, align: 'xMidYMid',
		reach: 0.5, band: 0.018, bands: 3, frames: centrePulse,
	},
	'top-left': { x: 0, y: 0, align: 'xMinYMin', ...corner },
	'top-right': { x: CANVAS, y: 0, align: 'xMaxYMin', ...corner },
	'bottom-left': { x: 0, y: CANVAS, align: 'xMinYMax', ...corner },
	'bottom-right': { x: CANVAS, y: CANVAS, align: 'xMaxYMax', ...corner },
};

const Canvas = styled.svg`
	display: block;
	width: 100%;
	height: 100%;
`;

const Ring = styled.g<{ $origin: string, $scale: number }>`
	transform-box: view-box;
	transform-origin: ${props => props.$origin};
	transform: scale(${props => props.$scale});
`;

const AnimatedRing = styled('use')<{
	$origin: string,
	$frames: ReturnType<typeof keyframes>,
	$duration: number,
	$delay: number,
}>`
	transform-box: view-box;
	transform-origin: ${props => props.$origin};
	/* Hidden until the animation drives it. Without this a ring renders full size the moment
	   it exists but is not yet animating — before its delay elapses, or on the first paint
	   if the styles land a frame late — which flashes as a hard line at full reach. */
	opacity: 0;
	animation: ${props => props.$frames}
		${props => props.$duration}s linear
		${props => props.$delay}s infinite;

	@media (prefers-reduced-motion: reduce) {
		/* Shift a whole loop back so the frozen frame is a ring mid-flight rather than one
		   that has not started, which would render at full size. */
		animation-delay: calc(${props => props.$delay}s - ${props => props.$duration}s);
		animation-play-state: paused;
	}
`;

export type PulseBackdropProps = {
	/** 'pulse' fires a short burst of bands, 'breathe' keeps them coming for half the loop. */
	animation?: PulseAnimation,
	/** Where the bands radiate from. */
	origin?: PulseOrigin,
	/** One colour, or a list the rings cycle through. Defaults to the three brand greys. */
	color?: string | string[],
	/** Concentric rings in each band. Fewer rings makes a thinner band. Defaults to 6. */
	rings?: number,
	/** Seconds for one full loop. Defaults to 6s for pulse, 10s for breathe. */
	duration?: number,
	className?: string,
}

export function PulseBackdrop({
	animation = 'pulse',
	origin = 'center',
	color = RING_COLORS,
	rings = DEFAULT_RINGS,
	duration,
	className,
}: PulseBackdropProps): React.ReactElement {
	const id = useUniqueId('pulse-backdrop-');
	const { x, y, align, reach, band: bandWindow, bands: bandCount, frames } = origins[origin];
	const originCss = `${x}px ${y}px`;
	const isBreathe = animation === 'breathe';
	const loop = duration ?? (isBreathe ? BREATHE_SECONDS : PULSE_SECONDS);
	const interval = isBreathe ? BREATHE_INTERVAL : PULSE_INTERVAL;
	const palette = Array.isArray(color) ? color : [color];
	const motifs = useMemo(() => ringMotifs(reach), [reach]);
	const motifScale = MOTIF_SIZE / reach / MOTIF_VIEWBOX;

	// A band is `rings` concentric rings: the origin keeps emitting them one after another, so
	// each ring is the same animation started a little later, which is what makes the band
	// widen as it picks up speed. The gap between rings is fixed, so dropping rings thins the
	// band rather than spreading the same width more thinly.
	// ponytail: 6 rings read sparser than the video's ~10-ring halftone at a corner origin.
	// Pass a higher `rings` if that shows.
	const gap = (isBreathe ? BREATHE_BAND : bandWindow) / (DEFAULT_RINGS - 1);
	const ringIndexes = Array.from({ length: Math.max(1, rings) }, (_, ring) => ring);
	// Delays count forwards so the first paint is empty and the first band grows out of the
	// origin. A negative delay would drop bands in mid-flight on mount, which reads as a hard
	// line appearing from nowhere.
	const bands = Array.from({ length: bandCount }, (_, i) => i * loop * interval);

	return (
		<Canvas
			className={className}
			viewBox={`0 0 ${CANVAS} ${CANVAS}`}
			preserveAspectRatio={`${align} meet`}
			aria-hidden="true"
			focusable="false"
		>
			<defs>
				{/* One ring per motif size. No fill, so each copy's own fill inherits in. */}
				{ringIndexes.map(ring => (
					<g key={ring} id={`${id}-${ring}`} transform={`translate(${x} ${y})`}>
						{motifs.map(motif => (
							<path
								key={motif.angle}
								d={MOTIF_PATH}
								transform={`translate(${motif.x} ${motif.y}) rotate(${motif.angle})`
									+ ` scale(${motifScale * ringMotifSize(ring, ringIndexes.length)})`
									+ ` translate(${-MOTIF_CENTER} ${-MOTIF_CENTER})`}
							/>
						))}
					</g>
				))}
			</defs>
			{bands.map((delay, i) => ringIndexes.map(ring => (
				<Ring key={`${i}-${ring}`} $origin={originCss} $scale={reach}>
					<AnimatedRing
						href={`#${id}-${ring}`}
						fill={palette[ring % palette.length]}
						$origin={originCss}
						$frames={isBreathe ? idleBand : frames}
						$duration={loop}
						$delay={delay + ring * loop * gap}
					/>
				</Ring>
			)))}
		</Canvas>
	);
}
